import { useEffect, useState } from "react";
import axios from "axios";
import Logo from "@/components/landing/Logo";
import { Loader2, LogOut, PhoneMissed, BadgeCheck, CalendarCheck2, Clock, PhoneForwarded, Users, Copy, Check } from "lucide-react";
import PortalMessages from "@/components/PortalMessages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_STYLES = {
  missed_call: "border-red-400/40 bg-red-400/10 text-red-400",
  sms_sent: "border-[#F25F22]/40 bg-[#F25F22]/10 text-[#F25F22]",
  qualifying: "border-amber-400/40 bg-amber-400/10 text-amber-400",
  qualified: "border-emerald-400/40 bg-emerald-400/10 text-emerald-400",
  escalated: "border-red-500/60 bg-red-500/15 text-red-400",
  booked: "border-emerald-500/60 bg-emerald-500/15 text-emerald-300",
};

const inputCls =
  "w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function Portal() {
  const [token, setToken] = useState(() => localStorage.getItem("bf_portal_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [convos, setConvos] = useState(null);
  const [open, setOpen] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  const logout = () => {
    localStorage.removeItem("bf_portal_token");
    setToken(null);
    setStats(null);
    setConvos(null);
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/portal/stats`, { headers })
      .then((r) => setStats(r.data))
      .catch((e) => { if (e.response?.status === 401 || e.response?.status === 403) logout(); });
    axios.get(`${API}/portal/conversations`, { headers })
      .then((r) => setConvos(r.data))
      .catch(() => setConvos([]));
  }, [token]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.user?.role !== "customer") {
        setError("This login is for Backfill customers. Admins use the /admin page.");
        return;
      }
      localStorage.setItem("bf_portal_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0D10] px-5">
        <form onSubmit={submitLogin} data-testid="portal-login-form" className="w-full max-w-sm rounded-xl border border-white/[0.1] bg-[#12161F] p-8">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="font-display text-lg font-bold text-slate-50">Customer Portal</span>
          </div>
          <p className="mt-2 text-xs text-slate-500">See every call Backfill recovered for your business.</p>
          <div className="mt-6 space-y-4">
            <input type="email" required data-testid="portal-email-input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <input type="password" required data-testid="portal-password-input" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          {error && <p data-testid="portal-login-error" className="mt-4 text-sm text-red-400">{error}</p>}
          <button type="submit" data-testid="portal-login-button" disabled={submitting} className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-60">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
          <a href="/" data-testid="portal-back-home" className="mt-4 block text-center text-xs text-slate-500 hover:text-slate-300">Back to site</a>
        </form>
      </div>
    );
  }

  const chips = stats ? [
    { icon: PhoneMissed, value: stats.this_week, label: "recovered this week", testid: "portal-chip-week" },
    { icon: BadgeCheck, value: stats.qualified, label: "qualified leads", testid: "portal-chip-qualified" },
    { icon: CalendarCheck2, value: stats.booked, label: "booked jobs", testid: "portal-chip-booked" },
    { icon: Clock, value: stats.total, label: "all-time recovered", testid: "portal-chip-total" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#0B0D10] px-5 py-10 sm:px-8" data-testid="portal-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <div>
              <h1 className="font-display text-xl font-bold text-slate-50">{stats?.company_name || "Your Recovered Calls"}</h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Backfill Customer Portal</p>
            </div>
          </div>
          <button onClick={logout} data-testid="portal-logout-button" className="flex items-center gap-2 rounded-md border border-white/[0.12] px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {chips.map((c) => (
            <div key={c.label} data-testid={c.testid} className="rounded-lg border border-white/[0.08] bg-[#12161F] p-4">
              <c.icon className="h-4 w-4 text-[#F25F22]" />
              <p className="mt-2.5 font-display text-2xl font-bold text-slate-50">{c.value}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">{c.label}</p>
            </div>
          ))}
        </div>

        {stats && (
          <div className="mt-6 rounded-xl border border-white/[0.08] bg-[#12161F] p-5" data-testid="portal-line-card">
            <div className="flex items-center gap-2">
              <PhoneForwarded className="h-4 w-4 text-[#F25F22]" />
              <h3 className="font-display text-sm font-semibold text-slate-50">Your Backfill line</h3>
            </div>
            {stats.tracking_number ? (
              <>
                <p className="mt-3 font-mono text-lg font-semibold text-emerald-400" data-testid="portal-tracking-number">
                  {stats.tracking_number}
                </p>
                <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-400">
                  <li>Open your business phone&apos;s dialer.</li>
                  <li>
                    Dial <span className="font-mono text-slate-200">*71{stats.tracking_number.replace("+1", "")}</span> (Verizon
                    busy/no-answer) or <span className="font-mono text-slate-200">*004*{stats.tracking_number.replace("+1", "")}#</span> (T-Mobile) —
                    or ask your phone provider for &quot;conditional call forwarding when busy or unanswered&quot; to your Backfill line.
                  </li>
                  <li>Miss a call on purpose — watch it appear below within seconds.</li>
                </ol>
              </>
            ) : (
              <p className="mt-3 text-sm text-slate-500" data-testid="portal-line-pending">
                We&apos;re setting up your dedicated line — it will appear here with 5-minute forwarding instructions.
              </p>
            )}
          </div>
        )}

        {stats?.referral_code && (
          <div className="mt-6 rounded-xl border border-white/[0.08] bg-[#12161F] p-5" data-testid="portal-referral-card">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[#F25F22]" />
              <h3 className="font-display text-sm font-semibold text-slate-50">Refer a contractor — earn a free month</h3>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-emerald-400" data-testid="portal-referral-count">
                {stats.referral_count || 0} earned
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Share your link with another contractor. When they become a paying customer,
              a free month is applied to your account automatically.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                readOnly
                value={`${window.location.origin}/signup?ref=${stats.referral_code}`}
                data-testid="portal-referral-link"
                onFocus={(e) => e.target.select()}
                className="w-full min-w-0 rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2.5 font-mono text-xs text-slate-300 outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${stats.referral_code}`);
                  setRefCopied(true);
                  setTimeout(() => setRefCopied(false), 2000);
                }}
                data-testid="portal-referral-copy"
                className="flex shrink-0 items-center gap-1.5 rounded-md bg-[#F25F22] px-3.5 py-2.5 text-xs font-semibold text-white hover:bg-[#FF7539]"
              >
                {refCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {refCopied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}

        <PortalMessages token={token} />

        <div className="mt-8 space-y-3">
          {convos === null ? (
            <div className="flex items-center justify-center gap-2 p-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          ) : convos.length === 0 ? (
            <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-12 text-center text-sm text-slate-500" data-testid="portal-empty">
              No recovered calls yet — they appear here the moment Backfill answers one.
            </div>
          ) : (
            convos.map((c) => (
              <div key={c.id} data-testid={`portal-convo-${c.id}`} className="rounded-xl border border-white/[0.08] bg-[#12161F] p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`portal-convo-toggle-${c.id}`} className="font-mono text-sm font-semibold text-slate-100 hover:text-[#F25F22]">
                    {c.caller_phone}
                  </button>
                  <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${STAGE_STYLES[c.stage] || ""}`}>
                    {c.stage.replace("_", " ")}
                  </span>
                  {c.service_type && <span className="truncate text-xs text-slate-400">· {c.service_type}</span>}
                  <span className="ml-auto text-xs text-slate-600">{new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                </div>
                {open === c.id && (
                  <div className="mt-4 space-y-2.5 border-t border-white/[0.06] pt-4">
                    {c.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-3.5 py-2 text-[13px] leading-relaxed ${m.from === "customer" ? "bg-[#F25F22] text-white" : "border border-white/[0.08] bg-[#1A212E] text-slate-200"}`}>
                          {m.body}
                        </div>
                      </div>
                    ))}
                    {c.notes && <p className="pt-1 text-xs text-slate-500">Availability notes: {c.notes}</p>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
